// Live's AI assistant at work (#242 part 2, spec G11 and docs/specs/editor/assistant.md): a
// message saved and sent with the project's saved version, the editor's labels and the chat so
// far; "Thinking" until the answer's job ends (the team stream's events, then the 15 s timer while
// the stream is down); the answer's summary saved; its plan applied, a step an undo step, each
// done or failed step's row saved, only when the project is still the version the assistant read;
// an Auto Captions step (#242) runs the editor's caption run on the plan's timeline and waits for
// its line, as live's executor waits for its skill; a Rough Cut step (#1001) then cuts by the words
// (spec assistant-rough-cut); a plan that searches the library (#1001) has its searches run and
// is asked again with the files found (spec assistant-search); and Revert, which puts back the
// timeline from before a plan (this session's plans only).
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { searchAssets } from "../../../api/generated/endpoints/assets/assets";
import {
  getAssistAnswer,
  getGetAssistAnswerQueryKey,
  startAssistAnswer,
} from "../../../api/generated/endpoints/editor/editor";
import type { AssistAnswer, AssistMessage, AssistPlan, AssistRequest, AssistTurn } from "../../../api/generated/model";
import { attemptKey } from "../../../platform/idempotency";
import { canChange, jobRefetchInterval } from "../../../platform/jobs/refetch";
import { toUserMessage } from "../../../platform/messages";
import { useStreamStatus } from "../../../platform/stream-status";
import type { Landed } from "../captions/use-generate-captions";
import { fetchSourceLength, serverKnows } from "../length";
import { sourceKey } from "../media";
import { ROUGH_CUT, roughCutSpans, type RoughCutOptions } from "../rough-cut";
import { refusal, spokenWords, type Timeline, type TimelineEdit } from "../timeline";
import { applyPlan, type ApplyContext } from "./apply";
import { foundFiles, offeredKey, withFound, type OfferedFile } from "./assist-media";
import { continueLine, searchFailed, searchParams, type LibrarySearch } from "./library-search";
import type { AssistChat, ChatProject } from "./use-assist-chat";

/** One of live's toasts, said in a line under the chat box. */
export interface Said {
  tone: "success" | "info" | "error";
  text: string;
}

/** G11's limits on what is sent. */
const MAX_TURNS = 20;
const MAX_TURN_CHARS = 3000;
const MAX_LABEL_CHARS = 200;

export const CHANGED = "The project changed while the assistant was answering. Ask again.";
/** Live's line after a failed step (its executor's last answer). */
export const PLAN_FAILED = "❌ I encountered an error while executing the plan.";

/** What the assistant reads and edits: the editor as it is now. */
export interface AssistantEditor {
  timeline: Timeline;
  /** The version the server holds, and whether the draft differs from it. */
  savedVersion: number;
  dirty: boolean;
  /**
   * The editor's undo history as it is: the same object while no edit, undo or redo is made, so
   * an edit made and undone while the assistant answers still counts as an edit (G11).
   */
  mark: unknown;
  /** Saves the draft when it differs: the version the server then holds, or undefined. */
  save: () => Promise<number | undefined>;
  /** What the editor shows each picture or sound as (its file's name), by clip id. */
  labels: Readonly<Record<string, string>>;
  /** The files the assistant may add (G11 PR 2): the media bin's newest, the first 50. */
  media: readonly OfferedFile[];
  /** How the plan's actions are made; the files offered are the ones sent with the message. */
  context: Omit<ApplyContext, "media">;
  /** The editor's caption run (#239) on this timeline: its line and words once it has landed. */
  captions: (from: Timeline) => Promise<Landed>;
  onEdit: (edit: TimelineEdit) => void;
}

function cut(text: string, most: number): string {
  return Array.from(text).slice(0, most).join("");
}

/** The chat so far as turns: the user's messages and the answers, not the step rows, the last 20. */
export function turnsOf(messages: readonly AssistMessage[]): AssistTurn[] {
  return messages
    .filter((message) => message.step === null)
    .slice(-MAX_TURNS)
    .map((message) => ({ role: message.role, text: cut(message.content, MAX_TURN_CHARS) }));
}

/** The labels as G11 takes them: each at most 200 characters. */
function labelsOf(labels: Readonly<Record<string, string>>): Record<string, string> {
  return Object.fromEntries(Object.entries(labels).map(([id, label]) => [id, cut(label, MAX_LABEL_CHARS)]));
}

/** What an ended answer may set: the line said, and a plan's timeline before it; and its searches. */
interface Landing {
say: (said: Said) => void;
planned: (messageId: string, before: Timeline) => void;
  /** A plan's library search, as the user: the files it found, offered as the bin offers them. */
  search: (search: LibrarySearch) => Promise<OfferedFile[]>;
  /** A file's length read from the file, as the bin reads it (spec assistant-file-length). */
  length: (file: OfferedFile) => Promise<number>;
}

/** What was sent: the answer's job, the saved version it reads, and the editor's history then. */
interface Asked {
  jobId: string;
  version: number;
  mark: unknown;
  /** The files offered with the message, by `offeredKey`: the plan may add only these. */
  media: ReadonlyMap<string, OfferedFile>;
  /** The message and the turns before it, as sent; a follow-up's message is the editor's. */
  message: string;
  history: readonly AssistTurn[];
  /** A library search's follow-up (spec assistant-search): its plan may not search again. */
  searched: boolean;
}

/** What a searching plan asks next (spec assistant-search): its message, its turns and files. */
interface FollowUp {
  message: string;
  history: AssistTurn[];
  media: readonly OfferedFile[];
}

/** An answer asked for: its job started, and what was asked, for the answer's landing. */
async function startAnswer(project: ChatProject, request: AssistRequest, offered: readonly OfferedFile[], mark: unknown, searched: boolean): Promise<Asked> {
  const started = await startAssistAnswer(project.teamId, project.brandId, project.projectId, request, {
    "Idempotency-Key": attemptKey(request, undefined),
  });
  const media = new Map(offered.map((file) => [offeredKey(file.wire.source), file]));
  return { jobId: started.job_id, version: request.version, mark, media, message: request.message, history: request.history ?? [], searched };
}

/**
 * A plan's rough cut, after its edits and its caption run: the spans by the words then (that run's,
 * else the captions' on the plan's timeline), worked out and checked on the timeline the editor
 * holds then (the run's, with any edit made while it ran, else the plan's) and cut through the
 * editor at once, as one more undo step; null, or why not. Nothing waits between the timeline it
 * is worked out on and the cut.
 */
async function roughCut(after: Timeline, captioning: Promise<Landed> | null, options: RoughCutOptions, at: AssistantEditor): Promise<string | null> {
  const landed = captioning === null ? null : await captioning;
  const timeline = landed === null ? after : landed.timeline;
  const words = landed === null ? spokenWords(after) : landed.words;
  if (timeline === null || words.length === 0) return ROUGH_CUT.noWords;
  const spans = roughCutSpans(timeline, words, options);
  if (spans.length === 0) return ROUGH_CUT.nothing(options.minSilenceMs);
  const edit: TimelineEdit = { type: "cutSpans", spans, idPrefix: at.context.newId() };
  const refused = refusal(timeline, edit);
  if (refused !== null) return refused;
  at.onEdit(edit);
  return null;
}

/**
 * The files offered, with the length of each one a plan's `add_clip` names read from its file: a
 * video or a sound the library has no positive length for (spec assistant-file-length), all at once. A
 * file whose read fails keeps none, and its step says so.
 */
async function withLengths(
  media: ReadonlyMap<string, OfferedFile>,
  plan: AssistPlan,
  read: (file: OfferedFile) => Promise<number>,
): Promise<ReadonlyMap<string, OfferedFile>> {
  const added = (plan.steps ?? []).flatMap((step) =>
    step.actions.flatMap((action) => (action.type === "add_clip" ? [offeredKey(action.source)] : [])),
  );
  const next = new Map(media);
  await Promise.all(
    [...new Set(added)].map(async (key) => {
      const file = media.get(key);
      if (file === undefined || file.kind === "image" || serverKnows(file.durationMs) || file.url === null) return;
      const durationMs = await read(file).catch(() => null);
      if (durationMs !== null) next.set(key, { ...file, durationMs });
    }),
  );
  return next;
}

/**
 * The answer said and saved, and its plan applied when the project is as it was read; after a
 * plan's searches, the follow-up to ask, else null. The editor is read (`now`) once the summary is
 * saved and the lengths are read, so nothing it awaits hides an edit from the check.
 */
async function land(done: AssistAnswer, asked: Asked, now: () => AssistantEditor, saving: AssistChat, landing: Landing): Promise<FollowUp | null> {
  if (done.status !== "succeeded" || done.plan === null) {
    landing.say({ tone: "error", text: "The assistant could not answer. Try again." });
    return null;
  }
  const plan = done.plan;
  try {
    const summary = await saving.save({ role: "assistant", content: plan.summary });
    if ((plan.steps ?? []).length === 0) return null;
    const media = await withLengths(asked.media, plan, landing.length);
    const at = now();
    // No edit since: the version sent, read by the assistant, and the same history now (G11).
    const changed = done.project_version !== asked.version || at.mark !== asked.mark || at.dirty;
    if (changed) {
      await saving.save({ role: "assistant", content: CHANGED });
      return null;
    }
    const { outcomes, timeline: after } = applyPlan(at.timeline, plan, { ...at.context, media, searched: asked.searched });
    for (const outcome of outcomes) {
      if (outcome.status === "done" && outcome.edit !== null) at.onEdit(outcome.edit);
    }
    // Revert for a plan that changes the timeline: a searching one changes nothing (its follow-up may).
    if (outcomes.some((outcome) => outcome.status === "done" && outcome.searches.length === 0)) {
      landing.planned(summary.id, at.timeline);
    }
    // Auto Captions: the run starts on the timeline the plan made, its words land as it is then.
    const captioning = outcomes.some((outcome) => outcome.status === "done" && outcome.captions)
      ? at.captions(after)
      : null;
    // The rough cut at once, as the caption run lands (else with the plan's edits) and before any
    // row is saved: nothing waits between the timeline it is worked out on and the cut.
    const cutting = outcomes.flatMap((outcome) => (outcome.status === "done" && outcome.roughCut !== null ? [outcome.roughCut] : []));
    const cutWhyNot = cutting[0] === undefined ? null : await roughCut(after, captioning, cutting[0], at);
    let failedAny = false;
    // A searching plan's searches, in turn: the files found and what each found, for the follow-up.
    const found: OfferedFile[] = [];
    const counts: { query: string; files: number }[] = [];
    for (const outcome of outcomes) {
      const { id, description } = outcome.step;
      let reason = outcome.status === "failed" ? outcome.reason : null;
      for (const search of outcome.status === "done" ? outcome.searches : []) {
        if (reason !== null) break;
        try {
          const files = await landing.search(search);
          found.push(...files);
          counts.push({ query: search.query, files: files.length });
        } catch (error) {
          reason = searchFailed(error);
        }
      }
      if (outcome.status === "done" && outcome.captions && captioning !== null) {
        const { said } = await captioning;
        landing.say(said);
        if (said.tone === "error") reason = said.text;
      }
      if (outcome.status === "done" && outcome.roughCut !== null && reason === null) reason = cutWhyNot;
      failedAny ||= reason !== null;
      await saving.save({
        role: "assistant",
        content: reason !== null ? `Failed: ${reason}` : description,
        step: { step_id: id, description, status: reason !== null ? "failed" : "done" },
      });
    }
    // Live's executor answers once more after a failed step, and its panel shows and saves it.
    if (failedAny) {
      await saving.save({ role: "assistant", content: PLAN_FAILED });
      return null;
    }
    if (counts.length === 0) return null;
    // About the same saved version: the turns with this message and its answer, the files found
    // first, and a message of the editor's that is not saved (decision 4).
    const turns: AssistTurn[] = [
      ...asked.history,
      { role: "user", text: cut(asked.message, MAX_TURN_CHARS) },
      { role: "assistant", text: cut(plan.summary, MAX_TURN_CHARS) },
    ];
    return { message: continueLine(counts), history: turns.slice(-MAX_TURNS), media: withFound(found, [...asked.media.values()]) };
  } catch (error) {
    landing.say({ tone: "error", text: `The chat could not be saved: ${toUserMessage(error)}` });
    return null;
  }
}

export function useAssistant(project: ChatProject, chat: AssistChat, editor: AssistantEditor) {
  const stream = useStreamStatus();
  const queryClient = useQueryClient();
  const [sending, setSending] = useState(false);
  const [asking, setAsking] = useState<Asked | null>(null);
  const [said, setSaid] = useState<Said | null>(null);
  // This session's plans: the timeline before each, by its answer's message.
  const [before, setBefore] = useState<ReadonlyMap<string, Timeline>>(new Map());
  // The editor and the chat as they are now, for an answer that ends later.
  const latest = useRef({ editor, chat, project });
  useEffect(() => {
    latest.current = { editor, chat, project };
  });

  const answer = useQuery({
    queryKey: getGetAssistAnswerQueryKey(project.teamId, project.brandId, project.projectId, asking?.jobId ?? ""),
    queryFn: ({ signal }) => getAssistAnswer(project.teamId, project.brandId, project.projectId, asking!.jobId, { signal }),
    enabled: asking !== null,
    refetchInterval: (query) => jobRefetchInterval(query.state.data?.status, stream),
  });
  const ended = asking !== null && answer.data !== undefined && !canChange(answer.data.status) ? answer.data : null;
  // What was asked, for the effect that lands its answer.
  const sent = useRef<Asked | null>(null);
  useEffect(() => {
    sent.current = asking;
  });
  const handled = useRef<string | null>(null);
  useEffect(() => {
    if (ended === null || handled.current === ended.job_id) return;
    handled.current = ended.job_id;
    const { editor: at, chat: saving, project: where } = latest.current;
    const asked = sent.current!;
    const landing: Landing = {
      say: setSaid,
      planned: (messageId, timeline) => setBefore((current) => new Map(current).set(messageId, timeline)),
      search: async (search) => {
        const page = await searchAssets(where.teamId, where.brandId, searchParams(search));
        return foundFiles(where.teamId, where.brandId, page.items, search.limit);
      },
      length: (file) => fetchSourceLength(queryClient, sourceKey(file.source), file.url ?? ""),
    };
    // After a search, the follow-up is asked at once: "Thinking" stays until it lands.
    const follow = async (next: FollowUp | null): Promise<Asked | null> => {
      if (next === null) return null;
      const request: AssistRequest = {
        message: next.message,
        version: asked.version,
        labels: labelsOf(at.labels),
        media: next.media.map((file) => file.wire),
        history: next.history,
      };
      try {
        return await startAnswer(where, request, next.media, asked.mark, true);
      } catch (error) {
        setSaid({ tone: "error", text: `The assistant could not be asked again: ${toUserMessage(error)}` });
        return null;
      }
    };
    void land(ended, asked, () => latest.current.editor, saving, landing)
      .then(follow, () => null)
      .then(
        (again) => setAsking(again),
        () => setAsking(null),
      );
  }, [ended, queryClient]);

  return {
    thinking: sending || asking !== null,
    said,
    /** This session's plans that Revert can undo, by their answer's message. */
    revertable: before,
    revert: (messageId: string) => {
      const timeline = before.get(messageId);
      if (timeline === undefined) return;
      editor.onEdit({ type: "restore", timeline });
      setBefore((current) => {
        const next = new Map(current);
        next.delete(messageId);
        return next;
      });
    },
    /** Live's Send: the message saved, the draft saved, the answer asked for. */
    send: async (text: string) => {
      const message = text.trim();
      if (message === "" || sending || asking !== null) return false;
      setSaid(null);
      setSending(true);
      try {
        const history = turnsOf(chat.messages);
        await chat.save({ role: "user", content: message });
        const version = await editor.save();
        if (version === undefined) {
          setSaid({ tone: "error", text: "The project could not be saved, so the assistant cannot read it. Try again." });
          return true;
        }
        const offered = editor.media;
        const request = { message, version, labels: labelsOf(editor.labels), media: offered.map((file) => file.wire), history };
        // The history as it was when the message was sent (the draft's save makes no edit).
        setAsking(await startAnswer(project, request, offered, editor.mark, false));
        return true;
      } catch (error) {
        setSaid({ tone: "error", text: `The message could not be sent: ${toUserMessage(error)}` });
        return true;
      } finally {
        setSending(false);
      }
    },
  };
}

export type Assistant = ReturnType<typeof useAssistant>;
